// Phase 3 C2 — CDD section for CaseView.
// Phase 9 Stage 2 (Item 9) — parameterised by `kind` ('case' | 'cc') so it
// also serves CollectiveCaseView; endpoints + permission ctx switch on kind.
//
// Section-as-dashboard with per-row modals. Rows:
//   1. Scope determination (red CTA when scopeStatus === 'required')
//   2. Risk assessment
//   3. Identification (read-mostly; references linked clients)
//   4. UBO chain (read-mostly; legal-person clients)
//   5. Vigilance triggers (deferred ID, high-risk country, tax fraud, PEP)
//   6. Mission letter (status + linked Document)
//   7. Atypical operations (list with add-new)
//   8. SAR (single row, RP-gated via caseView.cdd.sar default)
//
// Each row shows: status pill, verifiedBy/At, edit button. Modals collect
// row-specific fields + optional document attachment via openOneDrivePicker
// (basePath = the case root folder).
//
// Permission gating uses the standard hook: caseView.cdd.{view, edit, sar,
// pep, report.export}. The case RP gets all of these via the RP shortcut in
// usePermissions (case-internal keys).

import React, { useState, useEffect, useMemo } from "react";
import { api } from "../api";
import { useTranslation } from "../i18n";
import { usePermissions } from "../hooks/usePermissions";
import { openOneDrivePicker } from "../lib/loadOnUse.mjs"; // 208 §1 — the picker loads the first time it opens
import { openClientFolder } from "../lib/clientFolder";
import {
  CheckCircle2,
  Circle,
  AlertTriangle,
  Clock,
  Ban,
  FileText,
  Folder,
  Pencil,
  Plus,
  Trash2,
  Download,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  X,
  Users,
  ChevronRight,
} from "lucide-react";
import { CT, RAD, FS } from "../lib/tokens";
import { ModalLayout, ListNotLoaded } from "../components/SharedUI";
import { useReadMark, usePageListContext, oneRow } from "../hooks/usePageList"; // 212 §22
import { ListLoadContext, PAGE_LISTS } from "../lib/listLoad";
import { choosableCo } from "../lib/collaboratorState";
import MenuSelect from "../components/MenuSelect"; // 154B §2
import { useConfirm } from "../components/useConfirmAndUndo";
import AmountField from "../components/AmountField"; // 208 §10 — the one amount field

// Status pill renderer — taxonomy: not-applicable / required / in-progress /
// complete / stale.
function StatusPill({ status }: any) {
  const { t } = useTranslation();
  const map = {
    "not-applicable": {
      color: "var(--tm)",
      bg: "rgba(120,120,120,.15)",
      icon: Ban,
      labelKey: "cdd.status.not_applicable",
      fallback: "Not applicable",
    },
    required: {
      color: CT.c_ef4444,
      bg: "rgba(239,68,68,.15)",
      icon: AlertTriangle,
      labelKey: "cdd.status.required",
      fallback: "Required",
    },
    "in-progress": {
      color: CT.c_f59e0b,
      bg: "rgba(245,158,11,.15)",
      icon: Clock,
      labelKey: "cdd.status.in_progress",
      fallback: "In progress",
    },
    complete: {
      color: CT.c_10b981,
      bg: "rgba(16,185,129,.15)",
      icon: CheckCircle2,
      labelKey: "cdd.status.complete",
      fallback: "Complete",
    },
    stale: {
      color: CT.c_a16207,
      bg: "rgba(161,98,7,.15)",
      icon: Circle,
      labelKey: "cdd.status.stale",
      fallback: "Stale",
    },
  };
  const cfg = map[status as keyof typeof map] || map["required"];
  const Icon = cfg.icon;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "2px 8px",
        borderRadius: RAD.r999,
        fontSize: FS.s10,
        fontWeight: 600,
        color: cfg.color,
        background: cfg.bg,
        border: `1px solid ${cfg.color}40`,
      }}
    >
      <Icon size={11} />
      {t(cfg.labelKey, { fallback: cfg.fallback })}
    </span>
  );
}

function formatDate(d: any) {
  if (!d) return "";
  try {
    const dd = new Date(d);
    const day = String(dd.getDate()).padStart(2, "0");
    const m = String(dd.getMonth() + 1).padStart(2, "0");
    return `${day}/${m}/${dd.getFullYear()}`;
  } catch {
    return "";
  }
}

function pname(co: any, id: any) {
  if (!id || !Array.isArray(co)) return "";
  const r = co.find((x) => x.id === id);
  if (!r) return "#" + id;
  return (
    [r.firstName, r.lastName].filter(Boolean).join(" ") || r.email || "#" + id
  );
}

// Generic per-row block. Children are the row-specific extras (free-form
// rendering inside the row body).
function CddRow({
  icon: Icon,
  title,
  status,
  verifiedById,
  verifiedAt,
  co,
  onEdit,
  canEdit,
  ctaWhenRequired,
  children,
}: any) {
  const isRequiredCta = ctaWhenRequired && status === "required";
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        padding: "10px 12px",
        marginBottom: 6,
        background: isRequiredCta ? "rgba(239,68,68,.06)" : "var(--bi)",
        border: `1px solid ${isRequiredCta ? "rgba(239,68,68,.35)" : "var(--br)"}`,
        borderRadius: RAD.r8,
      }}
    >
      <div style={{ marginTop: 2 }}>
        <Icon
          size={14}
          style={{ color: isRequiredCta ? CT.c_ef4444 : "var(--ac)" }}
        />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 2,
          }}
        >
          <span style={{ fontSize: FS.s12, fontWeight: 600, color: "var(--tp)" }}>
            {title}
          </span>
          <StatusPill status={status || "required"} />
          {verifiedById && (
            <span style={{ fontSize: FS.s10, color: "var(--tm)" }}>
              {pname(co, verifiedById)}
              {verifiedAt ? ` · ${formatDate(verifiedAt)}` : ""}
            </span>
          )}
        </div>
        {children && (
          <div style={{ fontSize: FS.s11, color: "var(--tm)" }}>{children}</div>
        )}
      </div>
      {canEdit && (
        <button
          type="button"
          className="btn btn-ghost"
          style={{ padding: "3px 10px", fontSize: FS.s10 }}
          onClick={onEdit}
        >
          <Pencil size={10} />
        </button>
      )}
    </div>
  );
}

// Row modal scaffold — single column, validates required fields with toast +
// early return per basis doc §4.3 / recon RQ-A A3.
function RowModal({ title, onCancel, onSave, children }: any) {
  return (
    <ModalLayout
      title={title}
      onClose={onCancel}
      onSave={onSave}
      maxWidth={560}
    >
      {children}
    </ModalLayout>
  );
}

const STATUSES = [
  "not-applicable",
  "required",
  "in-progress",
  "complete",
  "stale",
];

function StatusSelect({ value, onChange }: any) {
  const { t } = useTranslation();
  return (
    <MenuSelect
      className="field-input"
      value={value || "required"}
      onChange={(v) => onChange(v)}
      options={STATUSES.map(
        (s) =>
          [s, t("cdd.status." + s.replace("-", "_"), { fallback: s })] as const,
      )}
    />
  );
}

export default function CddCaseSection({
  caseRow,
  co,
  currentUserId,
  onCaseRpChanged,
  kind = "case",
  nav,
}: any) {
  const { t } = useTranslation();
  const askConfirm = useConfirm();
  const { can, viewer } = usePermissions();
  // Phase 9 Stage 2 — `kind` parameterises Case vs CollectiveCase. caseId is
  // the parent id either way; the endpoint base + permission ctx switch on it.
  const isCc = kind === "cc";
  const caseId = caseRow?.id;
  const ctx = isCc ? { ccId: caseId } : { caseId };
  const stateBase = isCc ? "/cdd/collective-case/" : "/cdd/case/";
  const atypicalParent = isCc ? { ccId: caseId } : { caseId };
  const canView = can("caseView.cdd.view", ctx);
  const canEdit = can("caseView.cdd.edit", ctx);
  const canSar = can("caseView.cdd.sar", ctx);
  const canPep = can("caseView.cdd.pep", ctx);
  const canExport = can("caseView.cdd.report.export", ctx);

  const [state, setState] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState<any>(null);
  // Stage 3 Round 2 #9 — collapsed by default. Header always visible; content
  // renders only when expanded.
  const [collapsed, setCollapsed] = useState(true);

  /* 212 §22 — the read asked the 210 §1 way (asked again, twice, a second
     apart, each failure on the console); still failing, the section says in
     its place that its read failed, with « Réessayer » — it no longer vanishes.
     Nothing is drawn that could be saved over the stored record. */
  const { entry: cddRead, again: cddAgain, ask: askCdd } = useReadMark(PAGE_LISTS.cdd);
  const cddLists = usePageListContext(cddRead);
  useEffect(() => {
    if (!caseId) return undefined;
    let live = true;
    setLoading(true);
    void askCdd("CDD", () => oneRow(api.get(stateBase + caseId))).then((rows) => {
      if (!live) return;
      if (rows) setState(rows[0]);
      setLoading(false);
    });
    return () => {
      live = false;
    };
  }, [caseId, cddAgain, askCdd]);

  if (!canView) return null;
  if (loading)
    return (
      <p style={{ fontSize: FS.s11, color: "var(--tm)" }}>
        {t("common.loading", { fallback: "Loading…" })}
      </p>
    );
  if (!state)
    return cddRead.failure ? (
      <div className="detail-card" data-read-failed={PAGE_LISTS.cdd}>
        <h4 style={{ margin: 0 }}>{t("cdd.section.title", { fallback: "Client Due Diligence (CDD)" })}</h4>
        <ListLoadContext.Provider value={cddLists}>
          <ListNotLoaded lists={[PAGE_LISTS.cdd]} words={t("common.failed_to_load", { fallback: "Failed to load" })} />
        </ListLoadContext.Provider>
      </div>
    ) : null;

  const patchState = async (patch: any) => {
    try {
      const res = (await api.patch(stateBase + caseId, patch)) as Record<string, unknown>;
      // The server returns the state row only; preserve atypicals + missionDoc from prior fetch.
      setState((prev: any) => ({ ...prev, ...res }));
      setModal(null);
      if (window.lxToast)
        window.lxToast(t("cdd.toast.scope_saved", { fallback: "CDD saved" }));
    } catch (e: any) {
      if (window.lxToast)
        // brief 38 §3 — `e.message` is now the resolved message (api.ts runs
        // the srvErr lookup before minting it) and `e.body.error` is the raw
        // wire text, so the order that used to be equivalent is no longer.
        window.lxToast("⚠ " + (e?.message || e?.body?.error || "Save failed"));
    }
  };

  // Phase 9 Item 5 — full refresh of the CDD state (used after CONFID access
  // list mutations, which change confidAccess + possibly confidRootFolder).
  const reloadState = async () => {
    try {
      const d = await api.get(stateBase + caseId);
      setState(d);
    } catch (e) {
      console.warn("[cdd] reload failed", e);
    }
  };

  const exportWord = async () => {
    try {
      const { Document, Packer, Paragraph, TextRun, HeadingLevel } =
        await import("docx");
      const { saveAs } = await import("file-saver" as any);
      const ttl = (k: any, fb: any) => t(k, { fallback: fb });
      const rowPara = (label: any, val: any) =>
        new Paragraph({
          children: [
            new TextRun({ text: label + ": ", bold: true }),
            new TextRun({ text: String(val ?? "—") }),
          ],
        });
      const doc = new Document({
        sections: [
          {
            children: [
              new Paragraph({
                heading: HeadingLevel.TITLE,
                text: "CDD Report — " + (caseRow.title || caseRow.ref || ""),
              }),
              new Paragraph({
                text: "Generated " + new Date().toLocaleString("en-GB"),
              }),
              new Paragraph({
                heading: HeadingLevel.HEADING_1,
                text: ttl("cdd.row.scope", "Scope determination"),
              }),
              rowPara(ttl("cdd.status.complete", "Status"), state.scopeStatus),
              rowPara(
                "AML subject",
                state.scopeAmlSubject == null
                  ? "unknown"
                  : state.scopeAmlSubject
                    ? "yes"
                    : "no",
              ),
              rowPara(
                ttl("cdd.scope.exception_applies", "Exception applies"),
                state.scopeExceptionApplies == null
                  ? "—"
                  : state.scopeExceptionApplies
                    ? "yes"
                    : "no",
              ),
              new Paragraph({
                heading: HeadingLevel.HEADING_1,
                text: ttl("cdd.row.risk", "Risk"),
              }),
              rowPara("Tier", state.riskTier),
              rowPara("Rationale", state.riskRationale),
              new Paragraph({
                heading: HeadingLevel.HEADING_1,
                text: ttl("cdd.row.triggers", "Vigilance triggers"),
              }),
              rowPara(
                ttl("cdd.trigger.deferred_id", "Deferred ID"),
                state.triggerDeferredId ? "yes" : "no",
              ),
              rowPara(
                ttl("cdd.trigger.high_risk_country", "High risk country"),
                state.triggerHighRiskCountry ? "yes" : "no",
              ),
              rowPara(
                ttl("cdd.trigger.tax_fraud", "Tax fraud"),
                state.triggerTaxFraud ? "yes" : "no",
              ),
              rowPara(
                ttl("cdd.trigger.pep", "PEP"),
                state.triggerPep ? "yes" : "no",
              ),
              new Paragraph({
                heading: HeadingLevel.HEADING_1,
                text: ttl("cdd.row.mission_letter", "Mission letter"),
              }),
              rowPara("Status", state.missionLetterStatus),
              rowPara(
                "Sent",
                state.missionLetterSentAt
                  ? formatDate(state.missionLetterSentAt)
                  : "—",
              ),
              new Paragraph({
                heading: HeadingLevel.HEADING_1,
                text: ttl("cdd.row.atypical", "Atypical operations"),
              }),
              ...(state.atypicals || []).map(
                (a: any) =>
                  new Paragraph({
                    text: `${a.type || "?"} — ${a.description || ""} (${a.status})`,
                  }),
              ),
              ...(canSar
                ? [
                    new Paragraph({
                      heading: HeadingLevel.HEADING_1,
                      text: ttl("cdd.row.sar", "SAR"),
                    }),
                    rowPara("Status", state.sarStatus),
                    rowPara(
                      "Filed",
                      state.sarFiledAt ? formatDate(state.sarFiledAt) : "—",
                    ),
                    rowPara("Narrative", state.sarNarrative || "—"),
                  ]
                : []),
            ],
          },
        ],
      });
      const blob = await Packer.toBlob(doc);
      saveAs(blob, `CDD-${caseRow.ref || caseId}.docx`);
    } catch (e: any) {
      console.error("[cdd] export failed", e);
      if (window.lxToast)
        window.lxToast("⚠ Export failed: " + (e.message || "unknown"));
    }
  };

  return (
    <div className="detail-card">
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: collapsed ? 0 : 8,
          cursor: "pointer",
        }}
        onClick={() => setCollapsed((v) => !v)}
      >
        <h4 style={{ margin: 0 }}>
          {t("cdd.section.title", { fallback: "Client Due Diligence (CDD)" })}
        </h4>
        <span style={{ fontSize: FS.s10, color: "var(--tm)", flex: 1 }}>
          {collapsed && state?.scopeStatus === "required" && (
            <span style={{ color: CT.c_ef4444, fontWeight: 600 }}>
              ·{" "}
              {t("cdd.cta.scope_short", {
                fallback: "Scope determination required",
              })}
            </span>
          )}
        </span>
        {!collapsed && canExport && (
          <button
            type="button"
            className="btn btn-ghost"
            onClick={(e) => {
              e.stopPropagation();
              exportWord();
            }}
            style={{ padding: "3px 10px", fontSize: FS.s10 }}
          >
            <Download size={11} />{" "}
            {t("cdd.btn.export", { fallback: "Export CDD report" })}
          </button>
        )}
        {collapsed ? (
          <ChevronDown size={14} style={{ color: "var(--tm)" }} />
        ) : (
          <ChevronUp size={14} style={{ color: "var(--tm)" }} />
        )}
      </div>
      {!collapsed && (
        <>
          <CddRow
            icon={AlertTriangle}
            title={t("cdd.row.scope", { fallback: "Scope determination" })}
            status={state.scopeStatus}
            verifiedById={state.scopeVerifiedById}
            verifiedAt={state.scopeVerifiedAt}
            co={co}
            onEdit={() => setModal({ kind: "scope" })}
            canEdit={canEdit}
            ctaWhenRequired
          >
            {state.scopeStatus === "required" &&
              t("cdd.cta.scope", {
                fallback: "Answer scope determination to unlock CDD workflow",
              })}
            {state.scopeAmlSubject === true && (
              <>
                {/* 59 §3 — a COMPACT BADGE, not the form label. `cdd.scope.*`
                    already owns this concept in its long form ("This matter is
                    subject to the AML Law (Art. 5, 28°)"), which is a sentence
                    and does not fit a badge, so the badge gets its own key in
                    the same family rather than borrowing one that would render
                    a paragraph here. */}
                {t("cdd.badge.aml_subject", { fallback: "AML-subject" })}
                {state.scopeExceptionApplies
                  ? t("cdd.badge.exception_applies", {
                      fallback: " — exception applies",
                    })
                  : ""}
              </>
            )}
            {state.scopeAmlSubject === false &&
              t("cdd.status.not_applicable", { fallback: "Not applicable" })}
          </CddRow>

          {/* Phase 9 Stage 2 ext#2 (Item D) — read-only per-linked-client
          identification status; editing happens in the client view. */}
          <LinkedClientsPanel
            linkedClients={state.linkedClients}
            co={co}
            nav={nav}
          />

          {/* Stage 3 fix #4 — non-AML-subject cases collapse to scope row + note.
          Other rows hidden, not shown as "Not applicable". */}
          {state.scopeAmlSubject === false && (
            <p
              style={{
                fontSize: FS.s11_5,
                color: "var(--tm)",
                fontStyle: "italic",
                padding: "8px 12px",
                background: "rgba(120,120,120,.05)",
                borderRadius: RAD.r8,
              }}
            >
              {t("cdd.scope.exception_covered_note", {
                fallback:
                  "This case is exception-covered (Art. 5, 28° / legal advice / defense / procedure) — no further CDD obligations under the OBFG framework.",
              })}
            </p>
          )}

          {/* Stage 3 fix #2 — derived row statuses below. amlActive = AML-subject;
          the helper drops to 'required' when AML and no data, 'in-progress'
          when AML and partial, 'complete' when verifier stamped. Pre-scope
          state hides the non-scope rows so the user focuses on the CTA. */}
          {state.scopeAmlSubject === true &&
            (() => {
              const amlActive = state.scopeAmlSubject === true;
              const deriveRowStatus = (
                storedStatus: any,
                verifiedById: any,
                verifiedAt: any,
                hasAnyData: any,
              ) => {
                if (!amlActive) return "not-applicable";
                if (storedStatus === "complete" || (verifiedById && verifiedAt))
                  return "complete";
                if (hasAnyData) return "in-progress";
                return "required";
              };
              const riskHasData =
                !!state.riskTier ||
                !!state.riskRationale ||
                !!state.vigilanceLevel;
              const triggersHasData = !!(
                state.triggerDeferredId ||
                state.triggerHighRiskCountry ||
                state.triggerTaxFraud ||
                state.triggerPep ||
                state.triggersNotes
              );
              const missionHasData =
                !!state.missionLetterDocId || !!state.missionLetterSentAt;
              const sarHasData = !!state.sarFiledAt || !!state.sarNarrative;
              const riskDerived = deriveRowStatus(
                state.riskStatus,
                state.riskVerifiedById,
                state.riskVerifiedAt,
                riskHasData,
              );
              const triggersDerived = deriveRowStatus(
                state.triggersStatus,
                state.triggersVerifiedById,
                state.triggersVerifiedAt,
                triggersHasData,
              );
              const missionDerived = deriveRowStatus(
                state.missionLetterStatus,
                state.missionLetterVerifiedById,
                state.missionLetterVerifiedAt,
                missionHasData,
              );
              const sarDerived = deriveRowStatus(
                state.sarStatus,
                state.sarVerifiedById,
                state.sarVerifiedAt,
                sarHasData,
              );
              return (
                <>
                  <CddRow
                    icon={CheckCircle2}
                    title={t("cdd.row.risk", { fallback: "Risk assessment" })}
                    status={riskDerived}
                    verifiedById={state.riskVerifiedById}
                    verifiedAt={state.riskVerifiedAt}
                    co={co}
                    onEdit={() => setModal({ kind: "risk" })}
                    canEdit={canEdit}
                  >
                    {state.riskTier && (
                      <>
                        {t("cdd.risk_tier.label", { fallback: "Tier:" })}{" "}
                        <strong>
                          {t("cdd.risk_tier." + state.riskTier, {
                            fallback: state.riskTier,
                          })}
                        </strong>
                      </>
                    )}
                  </CddRow>

                  <CddRow
                    icon={AlertTriangle}
                    title={t("cdd.row.triggers", {
                      fallback: "Vigilance triggers",
                    })}
                    status={triggersDerived}
                    verifiedById={state.triggersVerifiedById}
                    verifiedAt={state.triggersVerifiedAt}
                    co={co}
                    onEdit={() => setModal({ kind: "triggers" })}
                    canEdit={canEdit}
                  >
                    {[
                      state.triggerDeferredId &&
                        t("cdd.trigger.deferred_id", {
                          fallback: "Deferred ID",
                        }),
                      state.triggerHighRiskCountry &&
                        t("cdd.trigger.high_risk_country", {
                          fallback: "High-risk country",
                        }),
                      state.triggerTaxFraud &&
                        t("cdd.trigger.tax_fraud", { fallback: "Tax fraud" }),
                      state.triggerPep &&
                        t("cdd.trigger.pep", { fallback: "PEP" }),
                    ]
                      .filter(Boolean)
                      .join(" · ") || "—"}
                  </CddRow>

                  <CddRow
                    icon={FileText}
                    title={t("cdd.row.mission_letter", {
                      fallback: "Mission letter",
                    })}
                    status={missionDerived}
                    verifiedById={state.missionLetterVerifiedById}
                    verifiedAt={state.missionLetterVerifiedAt}
                    co={co}
                    onEdit={() => setModal({ kind: "mission" })}
                    canEdit={canEdit}
                  >
                    {state.missionDoc && (
                      <>
                        <a
                          href={state.missionDoc.link}
                          target="_blank"
                          rel="noopener"
                          style={{ color: "var(--ac)" }}
                        >
                          {state.missionDoc.name}
                        </a>
                        {state.missionLetterSentAt && (
                          <>{t("cdd.mission_letter.sent_on", { d: formatDate(state.missionLetterSentAt), fallback: " · sent {d}" })}</>
                        )}
                      </>
                    )}
                  </CddRow>

                  <div style={{ marginBottom: 6 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "8px 12px",
                        background: "var(--bi)",
                        border: "1px solid var(--br)",
                        borderRadius: RAD.r8,
                      }}
                    >
                      <FileText size={14} style={{ color: "var(--ac)" }} />
                      <span style={{ fontSize: FS.s12, fontWeight: 600, flex: 1 }}>
                        {t("cdd.row.atypical", {
                          fallback: "Atypical operations",
                        })}
                      </span>
                      {canEdit && (
                        <button
                          type="button"
                          className="btn btn-ghost"
                          style={{ padding: "3px 10px", fontSize: FS.s10 }}
                          onClick={() => setModal({ kind: "atypical_new" })}
                        >
                          <Plus size={10} />{" "}
                          {t("cdd.btn.add_atypical", { fallback: "Add" })}
                        </button>
                      )}
                    </div>
                    {(state.atypicals || []).map((a: any) => {
                      // Stage 3 fix #2 — derive individual atypical-op status too.
                      const aHasData = !!(
                        a.description ||
                        a.amount ||
                        a.occurredAt
                      );
                      const aDerived = deriveRowStatus(
                        a.status,
                        a.verifiedById,
                        a.verifiedAt,
                        aHasData,
                      );
                      return (
                        <div
                          key={a.id}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            padding: "6px 12px",
                            marginLeft: 22,
                            fontSize: FS.s11,
                            borderBottom: "1px solid var(--br)",
                          }}
                        >
                          <span style={{ flex: 1 }}>
                            {t("cdd.atypical." + (a.type || "other"), {
                              fallback: a.type || "?",
                            })}
                            : {a.description || "—"}
                          </span>
                          <StatusPill status={aDerived} />
                          {canEdit && (
                            <button
                              type="button"
                              className="btn btn-ghost"
                              style={{ padding: "2px 6px", fontSize: FS.s9 }}
                              onClick={() =>
                                setModal({ kind: "atypical_edit", row: a })
                              }
                            >
                              <Pencil size={9} />
                            </button>
                          )}
                          {canEdit && (
                            <button
                              type="button"
                              className="btn"
                              style={{
                                padding: "2px 6px",
                                fontSize: FS.s9,
                                background: "rgba(248,113,113,.1)",
                                color: "var(--rd)",
                                border: "1px solid rgba(248,113,113,.3)",
                              }}
                              onClick={() => {
                                askConfirm({
                                  // 161C §3
                                  title: t("confirm.delete_atypical_title", {
                                    fallback: "Delete the atypical operation?",
                                  }),
                                  message: t("cdd.atypical.delete_confirm", {
                                    fallback: "Delete this atypical operation?",
                                  }),
                                  confirmLabel: t("common.delete", {
                                    fallback: "Delete",
                                  }),
                                  onConfirm: async () => {
                                    try {
                                      await api.del("/cdd/atypical/" + a.id);
                                      setState((prev: any) => ({
                                        ...prev,
                                        atypicals: prev.atypicals.filter(
                                          (x: any) => x.id !== a.id,
                                        ),
                                      }));
                                    } catch (e: any) {
                                      // Phase 30 (D-3) — was a silent unhandled
                                      // rejection.
                                      window.__lxToast?.(
                                        "Delete failed: " + (e?.message || e),
                                        "error",
                                      );
                                    }
                                  },
                                });
                              }}
                            >
                              <Trash2 size={9} />
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {canSar && (
                    <CddRow
                      icon={AlertTriangle}
                      title={t("cdd.row.sar", {
                        fallback: "Suspicious Activity Report (SAR)",
                      })}
                      status={sarDerived}
                      verifiedById={state.sarVerifiedById}
                      verifiedAt={state.sarVerifiedAt}
                      co={co}
                      onEdit={() => setModal({ kind: "sar" })}
                      canEdit={canSar}
                    >
                      {state.sarFiledAt && (
                        <>{t("cdd.sar.filed_on", { d: formatDate(state.sarFiledAt), fallback: "Filed {d}" })}</>
                      )}
                    </CddRow>
                  )}

                  {/* Phase 36 (E) — the per-case CONFID folder + access-list
                      manager was retired. The per-case CONFID folder is gone
                      (provisioning consolidated to the per-client folder) and
                      the SharePoint access/grant system is deferred to the next
                      phase. Folder links now live per linked client in the
                      Client Identification panel above
                      (LinkedClientsPanel → clientRootFolder). */}
                </>
              );
            })()}

          {modal?.kind === "scope" && (
            <ScopeModal
              state={state}
              caseRow={caseRow}
              co={co}
              onCancel={() => setModal(null)}
              onSave={(patch: any) =>
                patchState(patch).then(
                  () => onCaseRpChanged && onCaseRpChanged(),
                )
              }
            />
          )}
          {modal?.kind === "risk" && (
            <RiskModal
              state={state}
              onCancel={() => setModal(null)}
              onSave={patchState}
            />
          )}
          {modal?.kind === "triggers" && (
            <TriggersModal
              state={state}
              onCancel={() => setModal(null)}
              onSave={patchState}
            />
          )}
          {modal?.kind === "mission" && (
            <MissionModal
              state={state}
              caseRow={caseRow}
              onCancel={() => setModal(null)}
              onSave={patchState}
            />
          )}
          {modal?.kind === "sar" && canSar && (
            <SarModal
              state={state}
              onCancel={() => setModal(null)}
              onSave={patchState}
            />
          )}
          {(modal?.kind === "atypical_new" ||
            modal?.kind === "atypical_edit") && (
            <AtypicalModal
              row={modal.row}
              atypicalParent={atypicalParent}
              onCancel={() => setModal(null)}
              onSaved={(newRow: any, isUpdate: any) => {
                setState((prev: any) => ({
                  ...prev,
                  atypicals: isUpdate
                    ? prev.atypicals.map((x: any) =>
                        x.id === newRow.id ? newRow : x,
                      )
                    : [...(prev.atypicals || []), newRow],
                }));
                setModal(null);
              }}
            />
          )}
        </>
      )}
    </div>
  );
}

// ---------------- Linked-client identification panel (Item D) ----------

// Read-only list of the clients linked to the parent Case/CC, each with its
// derived CDD identification status. Editing identification stays in the
// client view; rows click through there when `nav` is supplied.
// Phase 36 (E) — open a linked client's SharePoint folder. The server resolves
// the webUrl only when the stored path matches and the viewer passes the
// per-client read-gate (admin OR RP on a matter the client is linked to); the
// path is already gated server-side (null unless visible) so a truthy
// clientRootFolder here already means "this viewer may open it".
// Phase 41 (D4) — the opener moved to src/lib/clientFolder.js (imported above)
// so the client record + Documents-section per-client rows reuse the SAME
// resolve-and-open path; no behavioural change here.

function LinkedClientsPanel({ linkedClients, co, nav }: any) {
  const { t } = useTranslation();
  const list = Array.isArray(linkedClients) ? linkedClients : [];
  const clickable = typeof nav === "function";
  return (
    <div
      style={{
        marginBottom: 6,
        padding: "10px 12px",
        background: "var(--bi)",
        border: "1px solid var(--br)",
        borderRadius: RAD.r8,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: list.length ? 6 : 0,
        }}
      >
        <Users size={14} style={{ color: "var(--ac)" }} />
        <span
          style={{ fontSize: FS.s12, fontWeight: 600, color: "var(--tp)", flex: 1 }}
        >
          {t("cdd.linked_clients.title", { fallback: "Client Identification" })}
        </span>
      </div>
      {list.length === 0 && (
        <p
          style={{
            fontSize: FS.s10_5,
            color: "var(--tm)",
            fontStyle: "italic",
            margin: "2px 0 0",
          }}
        >
          {t("cdd.linked_clients.empty", {
            fallback: "No clients linked to this case.",
          })}
        </p>
      )}
      {list.map((c: any) => (
        <div
          key={c.id}
          onClick={clickable ? () => nav("cl", c.id) : undefined}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "5px 0",
            borderBottom: "1px solid var(--br)",
            cursor: clickable ? "pointer" : "default",
          }}
          title={
            clickable
              ? t("cdd.linked_clients.open", { fallback: "Open client view" })
              : undefined
          }
        >
          <span
            style={{
              fontSize: FS.s11,
              color: "var(--tp)",
              flex: 1,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {c.name}
          </span>
          {c.identityVerifiedById && (
            <span style={{ fontSize: FS.s9_5, color: "var(--tm)" }}>
              {pname(co, c.identityVerifiedById)}
              {c.identityVerifiedAt
                ? " · " + formatDate(c.identityVerifiedAt)
                : ""}
            </span>
          )}
          {/* Phase 36 (E) — per-client folder link. Present only when the
              viewer passes the server-side read-gate (path is null otherwise). */}
          {c.clientRootFolder && (
            <button
              type="button"
              className="btn btn-ghost"
              title={t("cdd.client_folder.open", {
                fallback: "Open client folder",
              })}
              style={{
                padding: "1px 6px",
                fontSize: FS.s9_5,
                display: "inline-flex",
                alignItems: "center",
                gap: 3,
                flexShrink: 0,
              }}
              onClick={(e) => {
                e.stopPropagation();
                openClientFolder(c.id, c.clientRootFolder, t);
              }}
            >
              <Folder size={10} />
              <ExternalLink size={9} />
            </button>
          )}
          <StatusPill status={c.identityStatus || "required"} />
          {clickable && (
            <ChevronRight
              size={12}
              style={{ color: "var(--tm)", flexShrink: 0 }}
            />
          )}
        </div>
      ))}
    </div>
  );
}

// ---------------- Per-row modals ------------------

function ScopeModal({ state, caseRow, co, onCancel, onSave }: any) {
  const { t } = useTranslation();
  const [f, setF] = useState<any>({
    scopeStatus: state.scopeStatus || "required",
    scopeAmlSubject: state.scopeAmlSubject ?? null,
    scopeExceptionApplies: state.scopeExceptionApplies ?? null,
    scopeExceptionNotes: state.scopeExceptionNotes || "",
    // Phase 9 Stage 3 r2 (Item 7) — multi-RP collection for AML-subject cases
    // with no RP yet; sent as a _setResponsiblePartnerIds array on save.
    _setResponsiblePartnerIds: [],
  });
  const hasRp =
    Array.isArray(state.confidResponsiblePartnerIds) &&
    state.confidResponsiblePartnerIds.length > 0;
  const needsRp = f.scopeAmlSubject === true && !hasRp;
  const save = () => {
    if (
      f.scopeAmlSubject === true &&
      !(f._setResponsiblePartnerIds || []).length &&
      !hasRp
    ) {
      if (window.lxToast)
        window.lxToast(
          t("cdd.toast.rp_required", {
            fallback: "⚠ RP required for AML-subject cases",
          }),
        );
      return;
    }
    onSave({
      scopeStatus: f.scopeStatus,
      scopeAmlSubject: f.scopeAmlSubject,
      scopeExceptionApplies: f.scopeExceptionApplies,
      scopeExceptionNotes: f.scopeExceptionNotes || null,
      ...(needsRp && (f._setResponsiblePartnerIds || []).length
        ? { _setResponsiblePartnerIds: f._setResponsiblePartnerIds.map(Number) }
        : {}),
    });
  };
  // Stage 3 fix #1 — AMLCO must be a qualified Partner. Hardcoded role-name
  // filter is scoped to this single UI gate with clear regulatory grounding
  // (AMLCO = lawyer in charge per OBFG framework). Match both legacy string
  // and current { name } shapes; case-insensitive per basis doc convention.
  const isPartnerRole = (r: any) => {
    const n = (typeof r === "string" ? r : r?.name) || "";
    return n.trim().toLowerCase() === "partner";
  };
  const partners = choosableCo(co).filter((c: any) => isPartnerRole(c.role));
  return (
    <RowModal
      title={t("cdd.row.scope", { fallback: "Scope determination" })}
      onCancel={onCancel}
      onSave={save}
    >
      <div className="field">
        <label>
          {t("cdd.scope.aml_subject", {
            fallback: "Subject to AML Law (Art. 5, 28°)",
          })}
        </label>
        <MenuSelect
          className="field-input"
          value={f.scopeAmlSubject === null ? "" : String(f.scopeAmlSubject)}
          onChange={(v) =>
            setF((p: any) => ({
              ...p,
              scopeAmlSubject: v === "" ? null : v === "true",
            }))
          }
          options={[
            ["", "—"],
            ["true", t("common.yes", { fallback: "Yes" })],
            ["false", t("common.no", { fallback: "No" })],
          ]}
        />
      </div>
      {f.scopeAmlSubject === true && (
        <>
          <div className="field">
            <label>
              {t("cdd.scope.exception_applies", {
                fallback: "Exception applies (litigation legal advice)",
              })}
            </label>
            <MenuSelect
              className="field-input"
              value={
                f.scopeExceptionApplies === null
                  ? ""
                  : String(f.scopeExceptionApplies)
              }
              onChange={(v) =>
                setF((p: any) => ({
                  ...p,
                  scopeExceptionApplies: v === "" ? null : v === "true",
                }))
              }
              options={[
                ["", "—"],
                ["true", t("common.yes", { fallback: "Yes" })],
                ["false", t("common.no", { fallback: "No" })],
              ]}
            />
          </div>
          {f.scopeExceptionApplies && (
            <div className="field">
              <label>
                {t("cdd.field.exception_notes", {
                  fallback: "Exception notes",
                })}
              </label>
              <textarea
                className="field-input"
                value={f.scopeExceptionNotes}
                onChange={(e) =>
                  setF((p: any) => ({ ...p, scopeExceptionNotes: e.target.value }))
                }
              />
            </div>
          )}
          {needsRp && (
            <div
              className="field"
              style={{
                padding: 10,
                background: "rgba(239,68,68,.08)",
                border: "1px solid rgba(239,68,68,.35)",
                borderRadius: RAD.r6,
              }}
            >
              <label style={{ color: CT.c_ef4444 }}>
                {t("cdd.rp_required.title", { fallback: "RP required" })}
              </label>
              <p
                style={{
                  fontSize: FS.s11,
                  color: "var(--tm)",
                  margin: "2px 0 6px",
                }}
              >
                {t("cdd.rp_required.msg", {
                  fallback:
                    "AML-subject cases require an assigned RP (acts as AMLCO).",
                })}
              </p>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 4,
                  marginBottom: 6,
                }}
              >
                {(f._setResponsiblePartnerIds || []).map((rid: any) => {
                  return (
                    <span
                      key={rid}
                      className="multi-chip"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        padding: "2px 4px",
                      }}
                    >
                      <span style={{ padding: "0 3px" }}>
                        {/* 75 §4 — a CHOSEN id renders from the FULL roster
                            (the file's own pname), never from the choosable
                            pool above: an already-chosen archived partner
                            must keep their name, not decay to "#id". */}
                        {pname(co, Number(rid))}
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          setF((p: any) => ({
                            ...p,
                            _setResponsiblePartnerIds: (
                              p._setResponsiblePartnerIds || []
                            ).filter((x: any) => x !== rid),
                          }))
                        }
                        style={{
                          marginLeft: 6,
                          padding: 0,
                          width: 16,
                          height: 16,
                          background: "rgba(248,113,113,.15)",
                          border: "1px solid rgba(248,113,113,.4)",
                          color: "var(--rd)",
                          borderRadius: RAD.r4,
                          cursor: "pointer",
                          fontSize: FS.s11,
                          lineHeight: "12px",
                          fontFamily: "inherit",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        ×
                      </button>
                    </span>
                  );
                })}
                {!(f._setResponsiblePartnerIds || []).length && (
                  <span style={{ fontSize: FS.s9, color: "var(--tm)" }}>
                    {t("common.none", { fallback: "None" })}
                  </span>
                )}
              </div>
              {/* 154B §2 — an ACTION select: value is permanently "",
                  the first row is the label, and picking a partner adds one. */}
              <MenuSelect
                className="field-input"
                value=""
                onChange={(picked) => {
                  if (!picked) return;
                  const v = Number(picked);
                  setF((p: any) => ({
                    ...p,
                    _setResponsiblePartnerIds: [
                      ...(p._setResponsiblePartnerIds || []),
                      v,
                    ],
                  }));
                }}
                options={[
                  ["", t("common.select_dots", { fallback: "Select…" })] as const,
                  ...partners
                    .filter(
                      (c: any) => !(f._setResponsiblePartnerIds || []).includes(c.id),
                    )
                    .map(
                      (c: any) =>
                        [
                          String(c.id),
                          [c.firstName, c.lastName].filter(Boolean).join(" ") ||
                            c.email,
                        ] as const,
                    ),
                ]}
              />
            </div>
          )}
        </>
      )}
      <div className="field">
        <label>{t("common.status", { fallback: "Status" })}</label>
        <StatusSelect
          value={f.scopeStatus}
          onChange={(v: any) => setF((p: any) => ({ ...p, scopeStatus: v }))}
        />
      </div>
    </RowModal>
  );
}

function RiskModal({ state, onCancel, onSave }: any) {
  const { t } = useTranslation();
  const [f, setF] = useState<any>({
    riskStatus: state.riskStatus || "required",
    riskTier: state.riskTier || "",
    riskRationale: state.riskRationale || "",
    vigilanceLevel: state.vigilanceLevel || "",
  });
  const save = () =>
    onSave({
      riskStatus: f.riskStatus,
      riskTier: f.riskTier || null,
      riskRationale: f.riskRationale || null,
      vigilanceLevel: f.vigilanceLevel || null,
    });
  return (
    <RowModal
      title={t("cdd.row.risk", { fallback: "Risk assessment" })}
      onCancel={onCancel}
      onSave={save}
    >
      <div className="field">
        <label>{t("cdd.row.risk", { fallback: "Tier" })}</label>
        <MenuSelect
          className="field-input"
          value={f.riskTier}
          onChange={(v) => setF((p: any) => ({ ...p, riskTier: v }))}
          options={[
            ["", "—"],
            ["low", t("cdd.risk_tier.low", { fallback: "Low" })],
            ["standard", t("cdd.risk_tier.standard", { fallback: "Standard" })],
            ["high", t("cdd.risk_tier.high", { fallback: "High" })],
          ]}
        />
      </div>
      <div className="field">
        <label>{t("cdd.field.rationale", { fallback: "Rationale" })}</label>
        <textarea
          className="field-input"
          value={f.riskRationale}
          onChange={(e) =>
            setF((p: any) => ({ ...p, riskRationale: e.target.value }))
          }
        />
      </div>
      <div className="field">
        <label>
          {t("cdd.vigilance.standard", { fallback: "Vigilance level" })}
        </label>
        <MenuSelect
          className="field-input"
          value={f.vigilanceLevel}
          onChange={(v) => setF((p: any) => ({ ...p, vigilanceLevel: v }))}
          options={[
            ["", "—"],
            ["simplified", t("cdd.vigilance.simplified", { fallback: "Simplified" })],
            ["standard", t("cdd.vigilance.standard", { fallback: "Standard" })],
            ["enhanced", t("cdd.vigilance.enhanced", { fallback: "Enhanced" })],
          ]}
        />
      </div>
      <div className="field">
        <label>{t("common.status", { fallback: "Status" })}</label>
        <StatusSelect
          value={f.riskStatus}
          onChange={(v: any) => setF((p: any) => ({ ...p, riskStatus: v }))}
        />
      </div>
    </RowModal>
  );
}

function TriggersModal({ state, onCancel, onSave }: any) {
  const { t } = useTranslation();
  const [f, setF] = useState<any>({
    triggerDeferredId: !!state.triggerDeferredId,
    triggerHighRiskCountry: !!state.triggerHighRiskCountry,
    triggerTaxFraud: !!state.triggerTaxFraud,
    triggerPep: !!state.triggerPep,
    triggersNotes: state.triggersNotes || "",
    triggersStatus: state.triggersStatus || "not-applicable",
  });
  const save = () => onSave(f);
  return (
    <RowModal
      title={t("cdd.row.triggers", { fallback: "Vigilance triggers" })}
      onCancel={onCancel}
      onSave={save}
    >
      {[
        "triggerDeferredId",
        "triggerHighRiskCountry",
        "triggerTaxFraud",
        "triggerPep",
      ].map((k, i) => {
        const labelKeys = [
          "cdd.trigger.deferred_id",
          "cdd.trigger.high_risk_country",
          "cdd.trigger.tax_fraud",
          "cdd.trigger.pep",
        ];
        const fallbacks = [
          "Deferred identification",
          "High-risk country",
          "Tax fraud indicators",
          "Politically exposed person (PEP)",
        ];
        return (
          <div
            key={k}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              margin: "4px 0",
            }}
          >
            <input
              type="checkbox"
              checked={f[k]}
              onChange={(e) => setF((p: any) => ({ ...p, [k]: e.target.checked }))}
            />
            <span style={{ fontSize: FS.s12 }}>
              {t(labelKeys[i], { fallback: fallbacks[i] })}
            </span>
          </div>
        );
      })}
      <div className="field">
        <label>{t("cdd.field.notes", { fallback: "Notes" })}</label>
        <textarea
          className="field-input"
          value={f.triggersNotes}
          onChange={(e) =>
            setF((p: any) => ({ ...p, triggersNotes: e.target.value }))
          }
        />
      </div>
      <div className="field">
        <label>{t("common.status", { fallback: "Status" })}</label>
        <StatusSelect
          value={f.triggersStatus}
          onChange={(v: any) => setF((p: any) => ({ ...p, triggersStatus: v }))}
        />
      </div>
    </RowModal>
  );
}

function MissionModal({ state, caseRow, onCancel, onSave }: any) {
  const { t } = useTranslation();
  const [f, setF] = useState<any>({
    missionLetterStatus: state.missionLetterStatus || "required",
    missionLetterSentAt: state.missionLetterSentAt
      ? new Date(state.missionLetterSentAt).toISOString().slice(0, 10)
      : "",
    missionLetterDocId: state.missionLetterDocId || null,
    _picked: null,
  });
  const pickDoc = async () => {
    try {
      const picked = await openOneDrivePicker({
        basePath: caseRow?.caseRootFolder || null,
        caseId: caseRow?.id || null,
      });
      if (picked?.webUrl && !picked.isFolder) {
        const created = (await api.post("/documents", {
          caseId: caseRow.id,
          name: picked.name,
          link: picked.webUrl,
          type: "mission_letter",
          date: new Date().toISOString().slice(0, 10),
        })) as { id: number } & Record<string, unknown>;
        setF((p: any) => ({
          ...p,
          missionLetterDocId: created.id,
          _picked: created,
        }));
      }
    } catch (e: any) {
      if (window.lxToast)
        window.lxToast("⚠ " + (e.message || "Picker failed"));
    }
  };
  const save = () =>
    onSave({
      missionLetterStatus: f.missionLetterStatus,
      missionLetterSentAt: f.missionLetterSentAt || null,
      missionLetterDocId: f.missionLetterDocId || null,
    });
  return (
    <RowModal
      title={t("cdd.row.mission_letter", { fallback: "Mission letter" })}
      onCancel={onCancel}
      onSave={save}
    >
      <div className="field">
        <label>
          {t("cdd.btn.attach_doc", { fallback: "Attached document" })}
        </label>
        <div style={{ display: "flex", gap: 6 }}>
          <button
            type="button"
            className="btn btn-ghost"
            style={{ padding: "4px 10px", fontSize: FS.s10 }}
            onClick={pickDoc}
          >
            <Folder size={11} /> {t("modal.doc.onedrive", { fallback: "OneDrive" })}
          </button>
          {(f._picked || state.missionDoc) && (
            <span style={{ fontSize: FS.s11, alignSelf: "center" }}>
              {(f._picked || state.missionDoc)?.name}
            </span>
          )}
        </div>
      </div>
      <div className="field">
        <label>{t("cdd.field.sent_at", { fallback: "Sent at" })}</label>
        <input
          className="field-input"
          type="date"
          value={f.missionLetterSentAt}
          onChange={(e) =>
            setF((p: any) => ({ ...p, missionLetterSentAt: e.target.value }))
          }
        />
      </div>
      <div className="field">
        <label>{t("common.status", { fallback: "Status" })}</label>
        <StatusSelect
          value={f.missionLetterStatus}
          onChange={(v: any) => setF((p: any) => ({ ...p, missionLetterStatus: v }))}
        />
      </div>
    </RowModal>
  );
}

function SarModal({ state, onCancel, onSave }: any) {
  const { t } = useTranslation();
  const [f, setF] = useState<any>({
    sarStatus: state.sarStatus || "not-applicable",
    sarFiledAt: state.sarFiledAt
      ? new Date(state.sarFiledAt).toISOString().slice(0, 10)
      : "",
    sarNarrative: state.sarNarrative || "",
  });
  const save = () =>
    onSave({
      sarStatus: f.sarStatus,
      sarFiledAt: f.sarFiledAt || null,
      sarNarrative: f.sarNarrative || null,
    });
  return (
    <RowModal
      title={t("cdd.row.sar", { fallback: "Suspicious Activity Report (SAR)" })}
      onCancel={onCancel}
      onSave={save}
    >
      <div className="field">
        <label>{t("common.status", { fallback: "Status" })}</label>
        <StatusSelect
          value={f.sarStatus}
          onChange={(v: any) => setF((p: any) => ({ ...p, sarStatus: v }))}
        />
      </div>
      <div className="field">
        <label>{t("cdd.field.filed_at", { fallback: "Filed at" })}</label>
        <input
          className="field-input"
          type="date"
          value={f.sarFiledAt}
          onChange={(e) => setF((p: any) => ({ ...p, sarFiledAt: e.target.value }))}
        />
      </div>
      <div className="field">
        <label>{t("cdd.field.narrative", { fallback: "Narrative" })}</label>
        <textarea
          className="field-input"
          rows={5}
          value={f.sarNarrative}
          onChange={(e) =>
            setF((p: any) => ({ ...p, sarNarrative: e.target.value }))
          }
        />
      </div>
    </RowModal>
  );
}

function AtypicalModal({ row, atypicalParent, onCancel, onSaved }: any) {
  const { t } = useTranslation();
  const isEdit = !!row?.id;
  const [f, setF] = useState<any>({
    type: row?.type || "other",
    description: row?.description || "",
    occurredAt: row?.occurredAt
      ? new Date(row.occurredAt).toISOString().slice(0, 10)
      : "",
    amount: row?.amount ?? "",
    status: row?.status || "required",
  });
  const save = async () => {
    if (!f.description?.trim()) {
      if (window.lxToast) window.lxToast("⚠ Description required");
      return;
    }
    const body = {
      ...atypicalParent,
      type: f.type,
      description: f.description.trim(),
      occurredAt: f.occurredAt || null,
      amount: f.amount === "" ? null : Number(f.amount),
      status: f.status,
    };
    try {
      const res = isEdit
        ? await api.patch("/cdd/atypical/" + row.id, body)
        : await api.post("/cdd/atypical", body);
      onSaved(res, isEdit);
    } catch (e: any) {
      if (window.lxToast) window.lxToast("⚠ " + (e.message || "Save failed"));
    }
  };
  return (
    <RowModal
      title={t("cdd.row.atypical", { fallback: "Atypical operation" })}
      onCancel={onCancel}
      onSave={save}
    >
      <div className="field">
        <label>{t("common.type", { fallback: "Type" })}</label>
        <MenuSelect
          className="field-input"
          value={f.type}
          onChange={(v) => setF((p: any) => ({ ...p, type: v }))}
          options={[
            [
              "cash_large",
              t("cdd.atypical.cash_large", {
                fallback: "Large cash transaction",
              }),
            ],
            [
              "unusual_pattern",
              t("cdd.atypical.unusual_pattern", { fallback: "Unusual pattern" }),
            ],
            [
              "third_party_payment",
              t("cdd.atypical.third_party", { fallback: "Third-party payment" }),
            ],
            ["other", t("cdd.atypical.other", { fallback: "Other" })],
          ]}
        />
      </div>
      <div className="field">
        <label>
          {t("cdd.field.description_required", { fallback: "Description *" })}
        </label>
        <textarea
          className="field-input"
          value={f.description}
          onChange={(e) => setF((p: any) => ({ ...p, description: e.target.value }))}
        />
      </div>
      <div className="field">
        <label>{t("cdd.field.date", { fallback: "Date" })}</label>
        <input
          className="field-input"
          type="date"
          value={f.occurredAt}
          onChange={(e) => setF((p: any) => ({ ...p, occurredAt: e.target.value }))}
        />
      </div>
      <div className="field">
        <label>{t("cdd.field.amount", { fallback: "Amount" })}</label>
        {/* 208 §10 — the one amount field (src/components/AmountField.tsx): typed with a decimal comma, shown « 1 234,50 » */}
        <AmountField
          value={f.amount}
          empty=""
          inputMode="decimal"
          onAmount={(n) => setF((p: any) => ({ ...p, amount: n == null ? "" : String(n) }))}
        />
      </div>
      <div className="field">
        <label>{t("common.status", { fallback: "Status" })}</label>
        <StatusSelect
          value={f.status}
          onChange={(v: any) => setF((p: any) => ({ ...p, status: v }))}
        />
      </div>
    </RowModal>
  );
}
